import { Text } from '@thanx/uikit/text'
import { Toggle } from '@thanx/uikit/toggle'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.settings.escalation_contacts')

// Bordered-card chrome around the toggle (Figma: the toggle sits in a white
// card, not bare on the page).
const CARD_PADDING = '20px'
const CARD_RADIUS = '8px'

// id linking the switch to its visible label via aria-labelledby, so the
// accessible name comes from the visible text rather than a duplicate aria-label
// (which screen readers would announce twice). Namespaced per instance: a
// Settings pane and an open config modal can hold two toggles at once, and a
// duplicate id would resolve both switches to the SAME label element. React 16
// has no useId.
const notifyLabelId = (instanceId: string) =>
  `notify-location-managers-label-${instanceId}`

// Single source of truth for the recipient-field relabeling driven by the
// notify-location-managers toggle. ON → the field is a *fallback* (location
// managers are the primary recipients); OFF → it is the recipient for *all*
// escalations. The pane consumes this to label its primary RecipientPicker so
// the toggle and the picker label never drift apart.
export const recipientFieldLabel = (notifyManagers: boolean): string =>
  notifyManagers ? t('recipient_field.fallback') : t('recipient_field.all')

export type NotifyLocationManagersToggleProps = {
  /**
   * REQUIRED (never optional-with-default): namespaces this instance's label id
   * so a second concurrent mount cannot collide with it. A default would
   * reintroduce the collision at whichever call site the author forgot.
   */
  instanceId: string
  value: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  /** Overrides the default `notify_managers_desc` copy below the label. */
  description?: string
  /**
   * AI-1284: the Order feedback Notifications pane puts the label column
   * FIRST and the switch LAST, pinned to the row's right edge — the reverse of
   * the original RAI card. Defaults to false so RAI's toggle-first order (and
   * the two other call sites behind EscalationContactsBody) is unchanged.
   *
   *   context: NOT `flexDirection: 'row-reverse'` — that flips the gap margin
   *   to the wrong side of whichever element ends up second, so the JSX
   *   children are swapped instead.
   */
  labelFirst?: boolean
  /**
   * AI-1284: links the switch to an external note explaining why it is
   * disabled (the Order feedback Notifications pane's written-feedback gate),
   * via aria-describedby on the underlying input. Optional and undefined by
   * default, so every other call site is unaffected.
   */
  ariaDescribedBy?: string
}

// Presentational notify-location-managers toggle (AI-32 / U2). Prop-driven — the
// data wiring (reading/persisting escalation_routing_notify_location_managers)
// lives in the pane (U6). Rendered as a real on/off switch: the design-system
// Toggle (baseui checkbox) does not expose role="switch" by itself, so the role
// + i18n aria-label are forwarded onto its native input via overrides (which
// baseui spreads last, so they win) to satisfy the WAI-ARIA switch pattern.
const NotifyLocationManagersToggle = ({
  instanceId,
  value,
  onChange,
  disabled,
  description,
  labelFirst = false,
  ariaDescribedBy,
}: NotifyLocationManagersToggleProps) => {
  const [css] = useStyletron()
  const labelId = notifyLabelId(instanceId)

  const toggle = (
    <Toggle
      checked={value}
      onChange={onChange}
      disabled={disabled}
      overrides={{
        Input: {
          props: {
            role: 'switch',
            'aria-labelledby': labelId,
            'aria-describedby': ariaDescribedBy,
          },
        },
      }}
    />
  )

  // Flex column so the bold label and grey description stack on separate
  // lines (Text.BodyText3 is inline, so without this they collide).
  const labelColumn = (
    <div className={css({ display: 'flex', flexDirection: 'column' })}>
      <Text.BodyText3 bold>
        <span id={labelId}>{t('notify_managers_label')}</span>
      </Text.BodyText3>
      <Text.BodyText3 color="grey70">
        {description ?? t('notify_managers_desc')}
      </Text.BodyText3>
    </div>
  )

  return (
    <div
      className={`bg-white ${css({
        display: 'flex',
        alignItems: 'center',
        // labelFirst pins the switch to the row's right edge; the default
        // (toggle-first) row hugs its two children at the left, as before.
        ...(labelFirst ? { justifyContent: 'space-between' } : {}),
        border: `1px solid ${palette.grey20}`,
        borderRadius: CARD_RADIUS,
        padding: CARD_PADDING,
      })}`}
    >
      {/* The `ml-s` gap always sits on whichever element renders SECOND, so
          swapping the children order (never row-reverse) is enough to move
          the gap to the correct side. */}
      {labelFirst ? (
        <>
          {labelColumn}
          <div className="ml-s">{toggle}</div>
        </>
      ) : (
        <>
          {toggle}
          <div className="ml-s">{labelColumn}</div>
        </>
      )}
    </div>
  )
}

export default NotifyLocationManagersToggle
